import React, { useState, useEffect, useRef } from 'react';
import {
  Download,
  X,
  FileText,
  Image as ImageIcon,
  File,
  AlertCircle,
  Loader2,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize,
  ExternalLink
} from 'lucide-react';

// Define a more robust type that can handle what comes from the API
export interface PreviewDocument {
  id?: string;
  url: string;
  originalFileName?: string;
  extension?: string;
  type?: string;
  documentType?: string;
  [key: string]: any;
}

interface FilePreviewModalProps {
  previewDocument: PreviewDocument | null;
  setPreviewDocument: (doc: PreviewDocument | null) => void;
  handleDownloadDocument?: (doc: PreviewDocument) => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  previewDocument,
  setPreviewDocument,
  handleDownloadDocument
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Fetch PDF as blob to bypass Content-Disposition headers
  useEffect(() => {
    if (previewDocument) {
      setIsLoading(true);
      setHasError(false);
      setZoom(1);
      setRotation(0);
      setBlobUrl(null);

      const extension = (previewDocument.extension || previewDocument.type?.split('/').pop() || '').toLowerCase();

      // For PDFs, fetch as blob to bypass Content-Disposition: attachment
      if (extension === 'pdf') {
        const fetchPdfBlob = async () => {
          try {
            // Try to fetch the PDF with CORS mode
            const response = await fetch(previewDocument.url, {
              mode: 'cors',
              credentials: 'omit', // S3 URLs don't need credentials
            });

            if (!response.ok) throw new Error('Failed to fetch PDF');

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setBlobUrl(url);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching PDF:', error);
            // Fallback: Use Google Docs Viewer as a proxy
            // This will work even if S3 has Content-Disposition: attachment
            const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(previewDocument.url)}&embedded=true`;
            setBlobUrl(googleViewerUrl);
            setIsLoading(false);
          }
        };

        fetchPdfBlob();
      }
    }

    // Cleanup blob URL on unmount or document change
    return () => {
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [previewDocument]);

  // Handle "Escape" key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewDocument(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [setPreviewDocument]);

  if (!previewDocument) return null;

  const extension = (previewDocument.extension || previewDocument.type?.split('/').pop() || '').toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension);
  const isPdf = extension === 'pdf';

  // Local download handler if prop is not provided
  const onDownload = () => {
    if (handleDownloadDocument) {
      handleDownloadDocument(previewDocument);
    } else {
      const link = document.createElement('a');
      link.href = previewDocument.url;
      link.download = previewDocument.originalFileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const Icon = isImage ? ImageIcon : isPdf ? FileText : File;
  const iconColor = isImage ? "text-blue-500" : isPdf ? "text-red-500" : "text-gray-500";
  const typeLabel = (previewDocument.documentType || previewDocument.type || extension || 'Document').replace(/_/g, ' ').toUpperCase();

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  const renderContent = () => {
    if (hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8 animate-in fade-in duration-300">
          <div className="bg-red-50 p-4 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Preview Unavailable</h3>
          <p className="text-gray-500 mb-8 max-w-sm">We couldn't load the preview for this file. It might be corrupted or in an unsupported format.</p>
          <button
            onClick={onDownload}
            className="px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium shadow-lg shadow-gray-200"
          >
            <Download className="w-4 h-4" />
            Download Instead
          </button>
        </div>
      );
    }

    if (isImage) {
      return (
        <div className="relative w-full h-full flex items-center justify-center overflow-hidden bg-gray-50/50">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
            </div>
          )}
          <div
            className="transition-transform duration-200 ease-out will-change-transform"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              maxWidth: '100%',
              maxHeight: '100%'
            }}
          >
            <img
              src={previewDocument.url}
              alt={previewDocument.originalFileName}
              className={`max-w-full max-h-[75vh] object-contain shadow-sm transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
            />
          </div>
        </div>
      );
    }

    if (isPdf) {
      return (
        <div className="relative w-full h-full flex flex-col bg-gray-50">
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-white">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-4" />
              <p className="text-gray-600 text-sm">Loading PDF preview...</p>
            </div>
          )}

          {/* Show helpful message for PDFs */}
          {/* <div className="bg-blue-50 border-b border-blue-200 p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-900 font-medium">PDF Preview</p>
              <p className="text-xs text-blue-700 mt-1">
                For the best experience, click "Open in New Tab" to view this PDF in your browser's native viewer.
              </p>
            </div>
            <button
              onClick={() => {
                if (blobUrl) {
                  window.open(blobUrl, '_blank');
                } else {
                  // Fallback to original URL if blob not ready
                  window.open(previewDocument.url, '_blank');
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium whitespace-nowrap"
            >
              <ExternalLink className="w-4 h-4" />
              Open in New Tab
            </button>
          </div> */}

          <div className="flex flex-col items-center justify-center h-full text-center p-12">
            <div className="mb-6 p-6 bg-gray-50 rounded-2xl">
              <Icon className={`w-16 h-16 ${iconColor}`} />
            </div>
            <p className="text-gray-900 font-semibold text-xl mb-2">
              {previewDocument.originalFileName}
            </p>
            <p className="text-gray-500 mb-8 max-w-xs mx-auto">
              For the best experience, click "Open in New Tab" to view this PDF in your browser's native viewer.
            </p>
            <button
              onClick={() => {
                if (blobUrl) {
                  window.open(blobUrl, '_blank');
                } else {
                  // Fallback to original URL if blob not ready
                  window.open(previewDocument.url, '_blank');
                }
              }}
              className="px-8 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 transition-all flex items-center gap-2 font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              Open in New Tab
            </button>
          </div>


          {/* Commented out PDF preview - showing centered message instead */}
          {/* {blobUrl && (
            <>
              {blobUrl.startsWith('blob:') ? (
                <embed
                  src={`${blobUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  type="application/pdf"
                  className="w-full flex-1 bg-white"
                />
              ) : (
                <iframe
                  src={blobUrl}
                  className="w-full flex-1 bg-white border-0"
                  title={previewDocument.originalFileName}
                />
              )}
            </>
          )} */}
        </div>
      );
    }

    // Default Fallback
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-12">
        <div className="mb-6 p-6 bg-gray-50 rounded-2xl">
          <Icon className={`w-16 h-16 ${iconColor}`} />
        </div>
        <p className="text-gray-900 font-semibold text-xl mb-2">
          {previewDocument.originalFileName}
        </p>
        <p className="text-gray-500 mb-8 max-w-xs mx-auto">
          This file type ({extension.toUpperCase()}) cannot be previewed directly.
        </p>
        <button
          onClick={onDownload}
          className="px-8 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 transition-all flex items-center gap-2 font-medium"
        >
          <Download className="w-5 h-5" />
          Download File
        </button>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 sm:p-6 transition-all duration-300"
      onClick={() => setPreviewDocument(null)}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 ring-1 ring-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white z-20 shrink-0">
          <div className="flex items-center gap-4 overflow-hidden">
            <div className="flex-shrink-0 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <Icon className={`w-6 h-6 ${iconColor}`} />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-gray-900 truncate max-w-md" title={previewDocument.originalFileName}>
                {previewDocument.originalFileName || 'Untitled Document'}
              </h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="bg-gray-100 px-2 py-0.5 rounded text-[11px] font-bold tracking-wider text-gray-600">
                  {extension.toUpperCase()}
                </span>
                <span>•</span>
                <span className="truncate">{typeLabel}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pl-4">
            {isImage && (
              <div className="hidden sm:flex items-center gap-1 bg-gray-50 p-1 rounded-xl mr-2 border border-gray-100">
                <button onClick={handleZoomOut} className="p-2 hover:bg-white hover:shadow-sm rounded-lg text-gray-600 transition-all" title="Zoom Out">
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs font-mono w-12 text-center text-gray-500">{Math.round(zoom * 100)}%</span>
                <button onClick={handleZoomIn} className="p-2 hover:bg-white hover:shadow-sm rounded-lg text-gray-600 transition-all" title="Zoom In">
                  <ZoomIn className="w-4 h-4" />
                </button>
                <div className="w-px h-4 bg-gray-200 mx-1"></div>
                <button onClick={handleRotate} className="p-2 hover:bg-white hover:shadow-sm rounded-lg text-gray-600 transition-all" title="Rotate">
                  <RotateCw className="w-4 h-4" />
                </button>
              </div>
            )}

            <button
              onClick={() => window.open(previewDocument.url, '_blank')}
              className="p-2.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-xl transition-all duration-200"
              title="Open in New Tab"
            >
              <ExternalLink className="w-5 h-5" />
            </button>

            <button
              onClick={onDownload}
              className="p-2.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-xl transition-all duration-200"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>
            <div className="w-px h-6 bg-gray-200 mx-1"></div>
            <button
              onClick={() => setPreviewDocument(null)}
              className="p-2.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
              title="Close (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-[#F8F9FA] p-4 flex items-center justify-center relative">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
